import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, X as XIcon, Package, PlugZap, UserPlus, FileText, FileDown, Send, PartyPopper, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { Pill } from "@/components/app/shared";
import { cn } from "@/lib/utils";

const STEPS = {
  catalog: { icon: Package, title: "Revisa tu catálogo de precios", desc: "Los 6 productos traen precios de ejemplo; ajústalos a tus tarifas reales.", cta: "Abrir catálogo" },
  google: { icon: PlugZap, title: "Conecta tu Google", desc: "Así la propuesta sale desde tu Gmail con el PDF adjunto. Si prefieres, puedes copiar el enlace y enviarlo tú.", cta: "Ir a Integraciones" },
  deal: { icon: UserPlus, title: "Agrega tu primer deal", desc: "Anunciante, contacto con su correo, valor estimado y tipo de pauta.", cta: "Agregar lead" },
  quote: { icon: FileText, title: "Arma la cotización", desc: "Dentro del deal, en «Cotizaciones» → «Nueva cotización». Elige productos del catálogo; el IVA se calcula solo.", cta: "Abrir mi deal" },
  pdf: { icon: FileDown, title: "Genera el PDF de la propuesta", desc: "Botón «Generar PDF» en la cotización. Revísalo antes de enviarlo.", cta: "Abrir mi deal" },
  send: { icon: Send, title: "Envía la propuesta", desc: "Botón «Enviar»: va por Gmail con el PDF y un enlace para que el cliente acepte con su firma.", cta: "Abrir mi deal" },
};

export default function FirstDealGuide({ onOpenCatalog, onNewDeal, onOpenDeal }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["guide-first-deal"],
    queryFn: async () => (await api.get("/guide/first-deal")).data,
    refetchInterval: (q) => (q?.state?.data?.visible && !q?.state?.data?.celebrate ? 5000 : false),
  });
  const act = useMutation({
    mutationFn: async (action) => api.post("/guide/first-deal", { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["guide-first-deal"] }),
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!data) return null;
  if (!data.visible) {
    if (!data.canRestore) return null;
    return (
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={() => act.mutate("restore")} data-testid="guide-restore-button">
          <Compass className="mr-1.5 h-3.5 w-3.5" /> Mostrar guía del primer deal
        </Button>
      </div>
    );
  }

  if (data.celebrate) {
    return (
      <section className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-butter/50 px-5 py-4" data-testid="guide-celebrate">
        <div className="flex items-center gap-3">
          <PartyPopper className="h-5 w-5" strokeWidth={1.75} />
          <div>
            <div className="text-sm font-semibold">¡Enviaste tu primera propuesta!</div>
            <div className="text-[12.5px] text-muted-foreground">
              Cuando {data.dealClient || "el cliente"} acepte y firme, el deal pasa a Ganado y la evidencia se archiva sola en Drive.
            </div>
          </div>
        </div>
        <Button onClick={() => act.mutate("ack")} disabled={act.isPending} data-testid="guide-ack-button">Listo</Button>
      </section>
    );
  }

  const steps = data.steps;
  const doneCount = steps.filter((s) => s.done).length;
  const current = steps.find((s) => !s.done && !s.optional) || steps.find((s) => !s.done);

  const run = (key) => {
    if (key === "catalog") { onOpenCatalog(); act.mutate("catalog_seen"); }
    else if (key === "google") navigate("/integraciones");
    else if (key === "deal") onNewDeal();
    else if (data.dealId) onOpenDeal(data.dealId);
  };

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="first-deal-guide">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-3">
          <Compass className="h-4 w-4" strokeWidth={1.75} />
          <div>
            <div className="text-sm font-semibold">Tu primer deal, paso a paso</div>
            <div className="text-[12px] text-muted-foreground">Cada paso se marca solo cuando lo haces. Termina al enviar tu primera propuesta.</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-muted-foreground" data-testid="guide-progress-label">{doneCount} de {steps.length}</span>
            <Progress value={(doneCount * 100) / steps.length} className="h-1.5 w-28" />
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ocultar guía" onClick={() => act.mutate("dismiss")} data-testid="guide-dismiss-button">
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ol className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-3">
        {steps.map((s, i) => {
          const meta = STEPS[s.key];
          const isCurrent = current?.key === s.key;
          const needsDeal = ["quote", "pdf", "send"].includes(s.key) && !data.dealId;
          const Icon = meta.icon;
          return (
            <li key={s.key} className={cn("flex gap-3 bg-card px-5 py-4", isCurrent && "bg-lilac/15")} data-testid={`guide-step-${s.key}`} data-done={s.done ? "true" : "false"}>
              <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[11px]",
                s.done ? "border-foreground bg-foreground text-background" : isCurrent ? "border-foreground" : "border-border text-muted-foreground")}>
                {s.done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className={cn("text-[13px] font-medium", s.done && "text-muted-foreground line-through")}>{meta.title}</span>
                  {s.optional && !s.done && <Pill tone="neutral" className="text-[10px]">recomendado</Pill>}
                </div>
                <p className="text-[12px] leading-relaxed text-muted-foreground">{meta.desc}</p>
                {!s.done && (
                  <Button
                    size="sm"
                    variant={isCurrent ? "default" : "ghost"}
                    className={cn("mt-1 h-8", !isCurrent && "px-2")}
                    onClick={() => run(s.key)}
                    disabled={needsDeal}
                    data-testid={`guide-step-${s.key}-action`}
                  >
                    {needsDeal ? "Primero agrega un deal" : meta.cta}
                    {!needsDeal && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
                  </Button>
                )}
                {s.done && ["quote", "pdf", "send"].includes(s.key) && data.dealClient && (
                  <div className="font-mono text-[10.5px] text-muted-foreground">{data.dealClient}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
